import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ErrorPage from '../page/error';

export default class RecoverBoundary extends Component {
    static propTypes = {
        onError: PropTypes.func,
        children: PropTypes.node,
        maxRetries: PropTypes.number,
    };

    static defaultProps = {
        onError: () => {},
        maxRetries: 3,
    };

    state = {
        error: null,
        attempts: 0,
        failedOnce: false, // track if children failed without retry
    };

    static getDerivedStateFromError(error) {
        return { error: error };
    }

    componentDidCatch(error, info) {
        window.retries = window.retries + 1 || 0;
        try {
            this.props.onError({ error, source: 'errorBoundary', info });
        } catch (e) {
            console.error('Error in onError handler', e);
        }
    }

    retry = () => {
        this.setState((prev) => ({
            error: null,
            attempts: prev.attempts + 1,
            failedOnce: false, // reset failure tracker
        }));
    };

    render() {
        const { error, attempts, failedOnce } = this.state;
        const { maxRetries, children } = this.props;

        if (error) {
            console.log('ErrorBoundary caught an error', error, attempts);

            // Detect persistent failure to avoid infinite loops
            if (failedOnce) {
                return <div>⚠ Persistent error detected. Stopping retries.</div>;
            }

            try {
                if (attempts >= maxRetries) {
                    return <ErrorPage error={error} />;
                }

                return (
                    <div className="p-4 text-center" role="alert" aria-live="polite">
                        <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column'}}>
                            <p>Recovering from an unexpected error…</p>
                            <button onClick={this.retry}>Retry now</button>
                        </div>
                        <ErrorPage error={error} />
                    </div>
                );
            } catch (err) {
                return err;
            }
        }

        try {
            // Render children with key to force remount on retry
            return <React.Fragment key={attempts}>{children}</React.Fragment>;
        } catch (err) {
            // If children throw on mount, mark as failedOnce
            console.error('RecoverBoundary mount failure', err);
            this.setState({ error: err, failedOnce: true });
            return null;
        }
    }
}
